<template>
  <div class="w-full h-full min-h-0 relative group cursor-pointer flex flex-col">
    <input
      :id="inputId"
      ref="fileRef"
      type="file"
      class="hidden"
      accept="audio/*,.mp3,.wav,.aac,.m4a"
      @change="onFileChange"
    />
    <label
      v-if="!audioUrl"
      :for="inputId"
      class="flex flex-col items-center justify-center w-full h-full min-h-[140px] rounded-3xl border-2 border-dashed border-[#1c4848]/10 bg-white hover:bg-[#1c4848]/[0.02] hover:border-[#1c4848]/30 transition-all group-hover:shadow-sm p-6"
      :class="isOver && 'border-[#1c4848]/30 bg-[#1c4848]/[0.02]'"
      @click="triggerInput"
      @dragover.prevent="isOver = true"
      @dragleave.prevent="isOver = false"
      @drop.prevent="onDrop"
    >
      <div class="flex items-center gap-4 mb-2">
        <div class="w-12 h-12 rounded-full bg-[#ecd79f]/20 flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-[#bfa563]">
            <path d="M9 18V5l12-2v13"/>
            <circle cx="6" cy="18" r="3"/>
            <circle cx="18" cy="16" r="3"/>
          </svg>
        </div>
        <div class="flex flex-col text-left">
          <span class="text-sm font-bold text-[#1c4848]">Drop MP3 file here</span>
          <span class="text-xs text-[#1c4848]/50">or click to browse from computer</span>
        </div>
      </div>
      <div class="w-full max-w-xs h-1.5 bg-[#1c4848]/5 rounded-full mt-4 overflow-hidden">
        <div class="h-full bg-[#1c4848]/20 rounded-full transition-all duration-500" :style="{ width: isOver ? '66%' : '0' }"></div>
      </div>
      <div class="flex justify-between w-full max-w-xs mt-2 text-[9px] font-bold text-[#1c4848]/30 uppercase tracking-wider">
        <span>MP3, WAV, AAC</span>
        <span>Max 100MB</span>
      </div>
    </label>
    <div
      v-else
      class="w-full flex-1 min-h-[100px] rounded-3xl border-2 border-[#1c4848]/10 bg-white p-4 flex flex-col gap-3"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs font-bold text-[#1c4848] truncate">{{ fileName }}</span>
        <button
          type="button"
          class="shrink-0 p-1.5 text-[#1c4848]/50 hover:text-[#1c4848] hover:bg-[#1c4848]/5 rounded-lg transition-colors"
          title="Replace file"
          @click.stop="clearFile"
        >
          <i class="pr pr-import-564564 text-sm block"></i>
        </button>
      </div>
      <AudioPlayer :src="audioUrl" class="w-full" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import AudioPlayer from '@/components/audio/AudioPlayer.vue';

const props = defineProps({
  modelValue: { type: [String, File], default: null },
  inputId: { type: String, default: 'audio-upload' },
});

const emit = defineEmits(['update:modelValue']);

const fileRef = ref(null);
const isOver = ref(false);

const audioUrl = computed(() => {
  const v = props.modelValue;
  if (typeof v === 'string' && v) return v;
  if (v instanceof File) return URL.createObjectURL(v);
  return '';
});

const fileName = computed(() => {
  const v = props.modelValue;
  if (v instanceof File) return v.name;
  if (typeof v === 'string') return v.split('/').pop() || 'Audio';
  return '';
});

let objectUrlToRevoke = null;
watch(audioUrl, (url, prev) => {
  if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
});

function triggerInput() {
  fileRef.value?.click();
}

function setFile(file) {
  if (!file || !/^audio\//.test(file.type)) return;
  emit('update:modelValue', file);
}

function onDrop(e) {
  isOver.value = false;
  const f = e.dataTransfer?.files?.[0];
  if (f && /^audio\//.test(f.type)) setFile(f);
}

function onFileChange(e) {
  const f = e.target?.files?.[0];
  if (f) setFile(f);
}

function clearFile() {
  emit('update:modelValue', null);
  if (fileRef.value) fileRef.value.value = '';
}
</script>
